(ns io.daskul.webapp.widgets.form.auto-fill
  "Auto-fill link widget: link an existing entity to populate form fields.

   Renders as:
   - Not linked: Button to open slide-over with search
   - Linked: Banner with entity name + Unlink button

   The slide-over contains a searchable Select. On selection + confirm,
   the related record is fetched and mapped to form fields via :auto-fill config."
  (:require
   [uix.core :as uix :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.api.http :as api]
   [io.daskul.webapp.widgets.form.lookup :as lookup]
   [io.daskul.webapp.widgets.layout.slide-over :refer [slide-over]]))

;; ============================================================================
;; Slide-over search (same pattern as customer-picker: Select + onSearchChange)
;; ============================================================================

(defn- fetch-options!
  "Fetch lookup options via api/request!. Calls on-result with [{:value :label}].
   Routes through the shared base URL + auth + 401 policy."
  [lookup-url q on-result on-done]
  (api/request!
   {:method :get
    :path lookup-url
    :params (when (seq q) {:q q})
    :on-success (fn [resp]
                  (on-result (vec (:items resp)))
                  (on-done))
    :on-error (fn [err]
                (js/console.error "Auto-fill search failed:" err)
                (on-done))}))

(defui link-search
  "Searchable Select inside the slide-over.
   Uses the same pattern as customer-picker: initial load + debounced search."
  [{:keys [lookup-url label on-link on-cancel]}]
  (let [[options set-options!] (uix/use-state [])
        [loading? set-loading!] (uix/use-state true)
        [selected set-selected!] (uix/use-state nil)
        [search-value set-search-value!] (uix/use-state "")
        timer-ref (uix/use-ref nil)

        do-search!
        (uix/use-callback
         (fn [q]
           (set-loading! true)
           (fetch-options! lookup-url q set-options! #(set-loading! false)))
         [lookup-url])

        handle-search
        (fn [q]
          (set-search-value! q)
          (when-let [t @timer-ref]
            (js/clearTimeout t))
          (when (>= (count q) 2)
            (reset! timer-ref
                    (js/setTimeout #(do-search! q) 300))))

        handle-change
        (fn [val]
          (if val
            (when-let [opt (first (filter #(= (:value %) val) options))]
              (set-selected! opt))
            (set-selected! nil)))]

    ;; Initial load on mount
    (uix/use-effect
     (fn []
       (do-search! "")
       js/undefined)
     [do-search! lookup-url])

    ($ ui/stack {:gap "md"}
      ($ ui/select
        {:label (str "Search " label)
         :searchable true
         :searchValue search-value
         :onSearchChange handle-search
         :data (clj->js (mapv (fn [{:keys [value label]}]
                                #js {:value value :label label})
                              options))
         :value (:value selected)
         :onChange handle-change
         :placeholder (if loading? "Loading..." "Type 2+ chars to search...")
         :nothingFoundMessage (if loading? "Searching..." "No results found")
         :clearable true})

      ($ ui/group {:justify "flex-end" :mt "md" :gap "sm"}
        ($ ui/button {:variant "subtle" :color "gray" :onClick on-cancel} "Cancel")
        ($ ui/button {:disabled (nil? selected)
                      :onClick #(on-link selected)}
          "Link")))))

;; ============================================================================
;; Auto-fill link widget
;; ============================================================================

(defui link-widget
  "Auto-fill link widget for form integration.

   Props:
   - field: map - field definition with :ui {:auto-fill {...} :lookup-url :label}
   - value: any - current FK value (nil = not linked)
   - on-change: fn - set the FK value
   - on-auto-fill: fn - called with {:values {} :readonly-keys #{}} or nil
   - disabled: boolean"
  [{:keys [field value on-change on-auto-fill disabled]}]
  (let [ui (:ui field)
        auto-fill (:auto-fill ui)
        lookup-url (:lookup-url ui)
        label (or (:label ui) "Entity")
        [open? set-open!] (uix/use-state false)
        [linked-label set-linked-label!] (uix/use-state nil)

        handle-link
        (fn [{:keys [value label]}]
          (set-open! false)
          (set-linked-label! label)
          (on-change value)
          (lookup/fetch-auto-fill! lookup-url value auto-fill on-auto-fill))

        handle-unlink
        (fn []
          (set-linked-label! nil)
          (on-change nil)
          (on-auto-fill nil))]

    ($ :<>
      (if (and value linked-label)
        ;; Linked state: banner
        ($ ui/alert {:variant "light" :color "teal" :mb "md"
                     :title (str "Linked to " linked-label)}
          (when-not disabled
            ($ ui/button {:variant "subtle" :color "gray" :size "xs"
                          :onClick handle-unlink}
              "Unlink")))
        ;; Not linked: button
        ($ ui/button {:variant "light" :mb "md" :fullWidth true
                      :disabled disabled
                      :onClick #(set-open! true)}
          (str "Link " label)))

      ($ slide-over
        {:open? open?
         :on-close #(set-open! false)
         :title (str "Link " label)
         :size "md"}
        (when open?
          ($ link-search
            {:lookup-url lookup-url
             :label label
             :on-link handle-link
             :on-cancel #(set-open! false)}))))))
